import React, { useState } from 'react';
import { Shield, CheckCircle2, XCircle, Lock, Edit, Users, Loader, Save, X } from 'lucide-react';
import { Client, Admin } from './types';
import { getStatusStyle, getStatusLabel } from './utils';
import { useAlert } from '../../../../components/AlertProvider';
import api from '../../../../services/api';

interface AdminTabProps {
  client: Client;
  onUpdate: (client: Client) => void;
}

export const AdminTab: React.FC<AdminTabProps> = ({ client, onUpdate }) => {
  const { showError, showSuccess } = useAlert();
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [apporteurs, setApporteurs] = useState<Admin[]>([]);
  const [selectedApporteurId, setSelectedApporteurId] = useState<number | null>(client.admin_id);
  const [loadingApporteurs, setLoadingApporteurs] = useState(false);

  const adminLabel = client.admin
    ? (client.admin.first_name && client.admin.last_name
      ? `${client.admin.first_name} ${client.admin.last_name}`
      : client.admin.email)
    : '—';

  const loadApporteurs = async () => {
    try {
      setLoadingApporteurs(true);
      const res = await api.get('/users/admins', { params: { role_filter: 'apporteur' } });
      const raw = res.data;
      const list = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.items)
          ? raw.items
          : Array.isArray(raw?.results)
            ? raw.results
            : [];
      setApporteurs(list);
    } catch (e) {
      console.error('Load apporteurs error:', e);
      showError('Erreur lors du chargement des apporteurs');
    } finally {
      setLoadingApporteurs(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setSelectedApporteurId(client.admin_id);
    void loadApporteurs();
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSelectedApporteurId(client.admin_id);
  };

  const handleSave = async () => {
    if (!selectedApporteurId) {
      showError('Veuillez sélectionner un apporteur');
      return;
    }

    try {
      setSaving(true);
      const res = await api.patch(`/clients/${client.id}`, { admin_id: selectedApporteurId });
      showSuccess('Apporteur mis à jour avec succès');
      setIsEditing(false);
      onUpdate(res.data);
    } catch (e: any) {
      console.error('Update apporteur error:', e);
      showError(e.response?.data?.detail || 'Erreur lors de la mise à jour de l\'apporteur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700">
        <div className="p-3 sm:p-6">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4 sm:mb-6 pb-3 sm:pb-4 border-b border-gray-200 dark:border-slate-700">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-emerald-100 dark:bg-emerald-900/20 rounded-lg flex items-center justify-center">
                <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-slate-100">Apporteur</h3>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusStyle(client.status)}`}>
                {client.status === 'actif' ? <CheckCircle2 className="w-3 h-3 mr-1" /> : (client.status === 'bloque' ? <Shield className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />)}
                {getStatusLabel(client.status)}
              </span>
              {!client.is_active && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  <Lock className="w-3 h-3 mr-1" />
                  Désactivé
                </span>
              )}
              {!isEditing && (
                <button
                  onClick={handleEdit}
                  className="inline-flex items-center px-2.5 sm:px-3 py-1 sm:py-1.5 text-sm font-medium text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors"
                >
                  <Edit className="w-4 h-4 sm:mr-1" />
                  <span className="hidden sm:inline">Modifier</span>
                </button>
              )}
            </div>
          </div>

          <div className="space-y-5">
            {!isEditing ? (
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                  <Users className="w-4 h-4 text-emerald-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-500 dark:text-slate-400 mb-1">Apporteur d'affaire</p>
                  <p className="text-base text-gray-900 dark:text-slate-100">{adminLabel}</p>
                  {client.admin?.email && (
                    <p className="text-sm text-gray-600 dark:text-slate-400">{client.admin.email}</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                    Sélectionner un apporteur
                  </label>
                  {loadingApporteurs ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader className="w-5 h-5 animate-spin text-emerald-600" />
                    </div>
                  ) : (
                    <select
                      value={selectedApporteurId || ''}
                      onChange={(e) => setSelectedApporteurId(Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                    >
                      <option value="">-- Sélectionner un apporteur --</option>
                      {apporteurs.map((apporteur) => (
                        <option key={apporteur.id} value={apporteur.id}>
                          {apporteur.first_name && apporteur.last_name
                            ? `${apporteur.first_name} ${apporteur.last_name}`
                            : apporteur.email}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="flex items-center gap-2 pt-2">
                  <button
                    onClick={handleSave}
                    disabled={saving || !selectedApporteurId}
                    className="inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {saving ? (
                      <>
                        <Loader className="w-4 h-4 mr-1 sm:mr-2 animate-spin" />
                        <span className="hidden sm:inline">Enregistrement...</span>
                        <span className="sm:hidden">Enreg...</span>
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-1 sm:mr-2" />
                        Enregistrer
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={saving}
                    className="inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 text-sm bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-200 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-700 disabled:opacity-50 transition-colors"
                  >
                    <X className="w-4 h-4 mr-1 sm:mr-2" />
                    Annuler
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
